import { Component, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { clearFailedImports } from "@/lib/lazyWithRetry";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

export class LazyLoadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Lazy load error:", error, errorInfo);
  }

  handleRetry = () => {
    const { retryCount } = this.state;
    
    // Clear any cached failed imports
    clearFailedImports();
    
    // First 3 retries: try in-place recovery with increasing delay
    if (retryCount < 3) {
      // Add a small delay before retry to let network stabilize
      setTimeout(() => {
        this.setState({ 
          hasError: false, 
          error: null, 
          retryCount: retryCount + 1 
        });
      }, 500 * (retryCount + 1)); // 500ms, 1000ms, 1500ms
    } else {
      // After 3 in-place retries, do a full page reload with cache bust
      const url = new URL(window.location.href);
      url.searchParams.set('_reload', Date.now().toString());
      window.location.href = url.toString();
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { retryCount } = this.state;

      return (
        <div className="flex min-h-[50vh] items-center justify-center p-4">
          <div className="text-center space-y-4">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
            <h2 className="text-lg font-semibold">Failed to load page</h2>
            <p className="text-sm text-muted-foreground max-w-md">
              There was a problem loading this page. This might be due to a network issue.
              {retryCount > 0 && ` (Attempt ${retryCount + 1})`}
            </p>
            <Button onClick={this.handleRetry} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              {retryCount < 2 ? "Retry" : "Reload Page"}
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
